/**
 * Language-neutral helpers for describing AWS EventBridge cron fields.
 * Used by the per-locale describers (cronToZh, cronToEn).
 *
 * Schedules are stored in UTC and described in UTC+8 (Beijing time).
 */

/** AWS DOW order: SUN=1 .. SAT=7. Index 0..6 into this list. */
export const DOW_NAMES = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

export const MONTH_NAMES = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

export function isWild(field: string): boolean {
  return field === '*' || field === '?';
}

export function utcHourToLocal(h: number): number {
  return ((h + 8) % 24 + 24) % 24;
}

/** UTC hour string -> two-digit UTC+8 hour, e.g. "1" -> "09". A `*` start of a step counts as 0. */
export function convertHourStr(h: string): string {
  return String(utcHourToLocal(h === '*' ? 0 : parseInt(h, 10))).padStart(2, '0');
}

/**
 * One comma-separated item of a cron field: a value (or `*`), a range `a-b`,
 * either optionally with a step `/n`. "MON-WED,FRI" has two items; "8-17/2" one.
 * A numeric ranged step ends at its last value, so "8-17/2" has end "16";
 * one that only fires at its start ("8-9/2") is just that value.
 */
export interface CronItem {
  start: string;
  end?: string;
  step?: string;
}

export function cronItems(field: string): CronItem[] {
  return field.split(',').map((item) => {
    const [range, step] = item.split('/');
    const [start, end] = range.split('-');
    if (!end || !step) return { start, end, step };
    const last = lastStepped(start, end, step);
    return last === start ? { start } : { start, end: last, step };
  });
}

function lastStepped(start: string, end: string, step: string): string {
  const [s, e, n] = [start, end, step].map(Number);
  if (![s, e, n].every(Number.isInteger) || n < 1 || e < s) return end;
  return String(s + Math.floor((e - s) / n) * n);
}

/**
 * Minute items with any step spelled out, for lists that mix steps with other
 * items: "0/15,30" -> 0, 15, 30, 45. Lists without a step are returned as is.
 */
export function minuteListItems(minute: string): CronItem[] {
  const items = cronItems(minute);
  if (items.length < 2 || !items.some((i) => i.step)) return items;
  const values = new Set<number>();
  for (const { start, end, step } of items) {
    if (!step) {
      if (!end) values.add(Number(start));
      else for (let m = Number(start); m <= Number(end); m++) values.add(m);
      continue;
    }
    const from = start === '*' ? 0 : Number(start);
    const to = end !== undefined ? Number(end) : 59;
    for (let m = from; m <= to; m += Number(step)) values.add(m);
  }
  return [...values].sort((a, b) => a - b).map((m) => ({ start: String(m) }));
}

/** A field that is one plain value, e.g. "30" but not "*", "0/15", "1-5" or "1,3". */
export function isSingleValue(field: string): boolean {
  return /^\d+$/.test(field);
}

/**
 * Determine whether converting the hour field from UTC to UTC+8 uniformly
 * crosses midnight (dayShift=1) or not (dayShift=0).
 *
 * dayShift=1 only when every hour the field fires at is 16..23 UTC, e.g. "23",
 * "16-23", "18,20" or "16/4" (16 and 20). A partial overlap (some hours cross,
 * some don't) or a wildcard returns 0 because we cannot shift the DOW
 * unconditionally.
 */
export function computeDayShift(hour: string): number {
  if (isWild(hour)) return 0;
  const hours = cronItems(hour).flatMap(utcHoursOf);
  return hours.length > 0 && hours.every((h) => h + 8 >= 24) ? 1 : 0;
}

/** The UTC hours one hour-field item fires at, e.g. "8-17/2" -> 8, 10, 12, 14, 16. */
function utcHoursOf({ start, end, step }: CronItem): number[] {
  const from = start === '*' ? 0 : parseInt(start, 10);
  const to = end !== undefined ? parseInt(end, 10) : step !== undefined ? 23 : from;
  const by = step !== undefined ? parseInt(step, 10) : 1;
  if ([from, to, by].some(Number.isNaN) || by < 1) return [NaN];
  const hours: number[] = [];
  for (let h = from; h <= to; h += by) hours.push(h);
  return hours;
}

/**
 * Index (0=SUN .. 6=SAT) of a DOW token, from a 3-letter name (MON) or the
 * AWS number (2=MON). Returns -1 when the token is not recognised.
 */
export function dowIndex(d: string): number {
  const nameIdx = DOW_NAMES.indexOf(d.toUpperCase());
  if (nameIdx >= 0) return nameIdx;
  const num = parseInt(d, 10);
  return num >= 1 && num <= 7 && String(num) === d ? num - 1 : -1;
}

/** dowIndex shifted forward by `shift` days, or -1 when the token is not recognised. */
export function shiftedDowIndex(d: string, shift: number): number {
  const idx = dowIndex(d);
  return idx < 0 ? -1 : (idx + shift) % 7;
}

/** Month number 1..12 from a 3-letter name (JAN) or a number, or NaN. */
export function monthNumber(m: string): number {
  const nameIdx = MONTH_NAMES.indexOf(m.toUpperCase());
  if (nameIdx >= 0) return nameIdx + 1;
  const n = parseInt(m, 10);
  return n >= 1 && n <= 12 ? n : NaN;
}
